import type { ColumnFiltersState, SortingState } from '@tanstack/react-table';
import { sortingStateKey } from './frozen-sorted-row-model';
import type { PageSizeOption } from './types';

/** A page index from a URL or a parent: never NaN, negative or fractional. */
export function sanitizePageIndex(n: number): number {
  return Number.isFinite(n) ? Math.max(0, Math.floor(n)) : 0;
}

/** The last 1-based page for a total at a page size; at least 1, never NaN. */
export function lastPageNumber(total: number, limit: number): number {
  return Math.max(1, Math.ceil(Math.max(0, total || 0) / Math.max(1, limit || 1)));
}

/** Move a page past the end to the last page. Left alone while there are no pages. */
export function clampPageIndex(pageIndex: number, pageCount: number): number {
  if (pageCount <= 0) return pageIndex;
  return Math.min(Math.max(0, pageIndex), pageCount - 1);
}

/**
 * What sends a browser-paged table back to page 1: the data array's identity (or the caller's
 * resetKey instead, so expanding, editing and live merges keep the page), the sort and the column
 * filters.
 */
export function pageResetDeps({
  data,
  resetKey,
  sorting,
  columnFilters,
}: {
  data: unknown;
  resetKey?: string | number;
  sorting: SortingState;
  columnFilters?: ColumnFiltersState;
}): readonly unknown[] {
  return [
    resetKey === undefined ? data : `key:${resetKey}`,
    sortingStateKey(sorting),
    JSON.stringify(columnFilters ?? []),
  ];
}

/** For tables whose page DataTable doesn't own: nothing resets it. */
export const NO_PAGE_RESET_DEPS: readonly unknown[] = Object.freeze([]);

export function sameDeps(a: readonly unknown[], b: readonly unknown[]): boolean {
  return a.length === b.length && a.every((value, i) => Object.is(value, b[i]));
}

export type PaginationSummary = {
  empty: boolean;
  /** 1-based first and last row shown, for "Showing from–to of total". */
  from: number;
  to: number;
  /** "pageNumber / pageCountShown"; 0 when there are no pages, as before. */
  pageNumber: number;
  pageCountShown: number;
};

/**
 * The pager footer's numbers. A bad index (NaN, negative) reads as page 1, and an index past the
 * end shows the last page's range, so the footer never says NaN, from > to, or "3 / 2".
 */
export function paginationSummary({
  pageIndex,
  pageSize,
  total,
  pageCount,
}: {
  pageIndex: number;
  pageSize: number;
  total: number;
  pageCount: number;
}): PaginationSummary {
  const index = sanitizePageIndex(pageIndex);
  const size = Math.max(1, pageSize || 1);
  const rows = Number.isFinite(total) ? Math.max(0, Math.floor(total)) : 0;
  const pages = Number.isFinite(pageCount) ? Math.max(0, Math.floor(pageCount)) : 0;
  const rangeIndex = Math.min(index, lastPageNumber(rows, size) - 1);
  return {
    empty: rows === 0,
    from: rows === 0 ? 0 : rangeIndex * size + 1,
    to: Math.min((rangeIndex + 1) * size, rows),
    pageNumber: pages === 0 ? 0 : Math.min(index + 1, pages),
    pageCountShown: Math.max(pages, 1),
  };
}

/**
 * The Rows box's options with the current size in them, so the box never shows blank. A size the
 * caller didn't offer goes in numeric order (All last); `addedCurrent` lets dev builds warn.
 */
export function pageSizeOptionsWithCurrent(
  options: PageSizeOption[],
  pageSize: number
): { options: PageSizeOption[]; addedCurrent: boolean } {
  const current: PageSizeOption = pageSize >= Number.MAX_SAFE_INTEGER ? 'all' : pageSize;
  if (options.includes(current)) return { options, addedCurrent: false };
  if (current !== 'all' && !(Number.isFinite(current) && current > 0)) {
    return { options, addedCurrent: false };
  }
  if (current === 'all') return { options: [...options, 'all'], addedCurrent: true };
  const at = options.findIndex((option) => option === 'all' || option > current);
  const next = [...options];
  next.splice(at === -1 ? next.length : at, 0, current);
  return { options: next, addedCurrent: true };
}

/**
 * The `pageSize` prop DataTable follows under browser paging: only a page that remembers the size
 * (it passes `onPageSizeChange`) controls it after mount; otherwise the prop only seeds the table.
 */
export function browserPagingSizeProp(
  config: { pageSize?: number; onPageSizeChange?: unknown } | undefined,
  controlled: boolean,
  manual: boolean
): number | undefined {
  if (!config || controlled || manual || !config.onPageSizeChange) return undefined;
  return config.pageSize;
}
